import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Services
import { artistService } from '../../services/artist';
import spotifyCacheService from '../../services/spotifyCache';

// Interfaces
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Pagination } from '../../interfaces/api';

const initialState: {
  albums: Album[];
  singles: Album[];
  compilations: Album[];
  filter: 'All' | 'Album' | 'Singles' | 'Compilations';
  artist: Artist | null;
  loading: boolean;
  loaded: {
    albums: boolean;
    singles: boolean;
    compilations: boolean;
  };
} = {
  artist: null,
  filter: 'All',
  loading: true,
  albums: [],
  singles: [],
  compilations: [],
  loaded: {
    albums: false,
    singles: false,
    compilations: false,
  },
};

const enableDiscographySpotifyFallback =
  process.env.REACT_APP_ENABLE_DISCOGRAPHY_SPOTIFY_FALLBACK === 'true';

const groupAlbumsByType = (entries: Album[]) => {
  const grouped = {
    albums: [] as Album[],
    singles: [] as Album[],
    compilations: [] as Album[],
  };

  for (const album of entries) {
    if (!album) continue;
    switch (album.album_type) {
      case 'single':
        grouped.singles.push(album);
        break;
      case 'compilation':
        grouped.compilations.push(album);
        break;
      default:
        grouped.albums.push(album);
        break;
    }
  }

  return grouped;
};

const fetchData = createAsyncThunk<
  { artist: Artist; albums: Album[]; singles: Album[]; compilations: Album[] },
  string
>(
  'discography/fetchData',
  async (id) => {
    const [cachedArtist, cachedDiscography] = await Promise.all([
      spotifyCacheService.fetchArtist(id).catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[discographySlice] Failed to load artist from cache', { id, error });
        }
        return null;
      }),
      spotifyCacheService.fetchArtistDiscography(id).catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[discographySlice] Failed to load discography from cache', { id, error });
        }
        return null;
      }),
    ]);

    let artist: Artist | null = cachedArtist;

    if (!artist) {
      const artistResponse = await artistService.fetchArtist(id);
      artist = artistResponse.data as Artist;
    }

    if (!artist) {
      throw new Error('Artist not found');
    }

    if (cachedDiscography?.albums?.length) {
      const grouped = groupAlbumsByType(cachedDiscography.albums as Album[]);
      return { artist, ...grouped };
    }

    if (enableDiscographySpotifyFallback) {
      const [albumsResponse, singlesResponse, compilationsResponse] = await Promise.all([
        artistService.fetchArtistAlbums(id, { limit: 50, include_groups: 'album' }),
        artistService.fetchArtistAlbums(id, { limit: 50, include_groups: 'single' }),
        artistService.fetchArtistAlbums(id, { limit: 50, include_groups: 'compilation' }),
      ]);

      const fallbackAlbums = (albumsResponse.data as Pagination<Album>).items as Album[];
      const fallbackSingles = (singlesResponse.data as Pagination<Album>).items as Album[];
      const fallbackCompilations = (compilationsResponse.data as Pagination<Album>).items as Album[];

      return {
        artist,
        albums: fallbackAlbums,
        singles: fallbackSingles,
        compilations: fallbackCompilations,
      };
    }

    // No cache hit and fallback disabled: return empty groups to avoid live Spotify calls.
    return { artist, albums: [], singles: [], compilations: [] };
  }
);

const fetchGroup = createAsyncThunk<
  { group: 'Album' | 'Singles' | 'Compilations'; items: Album[] },
  { artistId: string; group: 'Album' | 'Singles' | 'Compilations' }
>('discography/fetchGroup', async ({ artistId, group }) => {
  const includeGroupsMap: Record<'Album' | 'Singles' | 'Compilations', 'album' | 'single' | 'compilation'> =
    {
      Album: 'album',
      Singles: 'single',
      Compilations: 'compilation',
    };

  const response = await artistService.fetchArtistAlbums(artistId, {
    limit: 50,
    include_groups: includeGroupsMap[group],
  });

  const items = (response.data as Pagination<Album>).items as Album[];
  return { group, items };
});

const artistDiscographySlice = createSlice({
  name: 'discography',
  initialState,
  reducers: {
    setArtist(state, action: PayloadAction<{ artist: Artist | null }>) {
      state.artist = action.payload.artist;
      if (!action.payload.artist) {
        state.albums = [];
        state.singles = [];
        state.compilations = [];
        state.loading = true;
        state.artist = null;
        state.loaded = {
          albums: false,
          singles: false,
          compilations: false,
        };
      }
    },
    setFilter(
      state,
      action: PayloadAction<{ filter: 'All' | 'Album' | 'Singles' | 'Compilations' }>
    ) {
      state.filter = action.payload.filter;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchData.fulfilled, (state, action) => {
      state.artist = action.payload.artist;
      state.albums = action.payload.albums;
      state.singles = action.payload.singles;
      state.compilations = action.payload.compilations;
      state.loaded.albums = action.payload.albums.length > 0;
      state.loaded.singles = action.payload.singles.length > 0;
      state.loaded.compilations = action.payload.compilations.length > 0;
      state.loading = false;
    });
    builder.addCase(fetchGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchGroup.fulfilled, (state, action) => {
      if (action.payload.group === 'Album') {
        state.albums = action.payload.items;
        state.loaded.albums = true;
      } else if (action.payload.group === 'Singles') {
        state.singles = action.payload.items;
        state.loaded.singles = true;
      } else if (action.payload.group === 'Compilations') {
        state.compilations = action.payload.items;
        state.loaded.compilations = true;
      }
      state.loading = false;
    });
    builder.addCase(fetchGroup.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const artistDiscographyActions = {
  fetchData,
  fetchGroup,
  ...artistDiscographySlice.actions,
};

export default artistDiscographySlice.reducer;
